// Queue alerts across a season (concessions): how many times queues reached the alert threshold
// and how fast they came back under it, game by game over a date range. Alerts are rebuilt from the
// saved queue readings with the backend's rule (see queueAlerts.js), so no extra backend route is needed.
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { getTeam } from '../../util.js';
import Icon from '../../components/atoms/Icon.js';
import { Panel, PanelHeader } from '../Concession/components/ConcessionUI';
import { applyChartTheme, hoverGuidePlugin } from '../Concession/charts/chartTheme';
import { KpiCard } from '../Concession/live/LiveComponents';
import { fetchLocationDay } from './analyticsData';
import { ALERT_AT, gameAlerts, summarizeAlerts, formatResponse, alertClock, isBlip } from './queueAlerts';

// One season's games load a few at a time so the API isn't hit with every request at once
const CONCURRENCY = 3;
const ALERT_COLOR = '#e88559';
const RESPONSE_COLOR = '#3e58c0';
const BLIP_COLOR = '#c4ccd0';

const RULE_TEXT = `An alert is sent when a section's queue is ${ALERT_AT} min or more (or overflowing) for two readings in a row, and a recovery when it's under ${ALERT_AT} min for two readings in a row. Rebuilt from each game's saved queue readings with the same rule.`;
const BLIP_TEXT = 'Blips: alerts where the queue was back under the target within a minute. Too fast to be a staff response, so they count as alerts sent but are left out of every response time.';

export default function QueueAlertsView({ authCtx, games, league, season, todayKey }) {
  const navigate = useNavigate();

  // Concession stands that measure queue times (not people counts)
  const stands = useMemo(() => (authCtx.authLocationData?.concessions || [])
    .filter((l) => l.sensorDataType !== 'count' && (l.sections || []).length > 0), [authCtx.authLocationData]);
  const [standIds, setStandIds] = useState(null); // null = every stand
  const chosen = stands.filter((s) => !standIds || standIds.includes(s.id));
  const toggleStand = (id) => setStandIds((prev) => {
    const current = prev || stands.map((s) => s.id);
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    return next.length === 0 || next.length === stands.length ? null : next;
  });

  // This season's games so far (today's counts while it's in progress), oldest first
  const seasonGames = useMemo(() => {
    const list = [];
    (games || []).forEach((series) => (series.dates || []).forEach((d) => {
      const date = moment(d.date).format('YYYY-MM-DD');
      if (date.slice(0, 4) === String(season) && date <= todayKey) list.push({ date, series });
    }));
    const seen = new Set();
    return list.filter((g) => (seen.has(g.date) ? false : seen.add(g.date))).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [games, season, todayKey]);
  const first = seasonGames[0]?.date || `${season}-01-01`;
  const last = seasonGames[seasonGames.length - 1]?.date || todayKey;

  const [range, setRange] = useState(null); // { from, to }; null = whole season
  const from = range?.from || first;
  const to = range?.to || last;
  const inRange = seasonGames.filter((g) => g.date >= from && g.date <= to);
  const presets = [
    { key: 'season', label: 'Whole season', range: null },
    { key: '30', label: 'Last 30 days', range: { from: moment(last).subtract(29, 'days').format('YYYY-MM-DD'), to: last } },
    { key: '10', label: 'Last 10 games', range: { from: seasonGames[Math.max(0, seasonGames.length - 10)]?.date || first, to: last } },
  ];
  const activePreset = presets.find((p) => (p.range ? p.range.from === from && p.range.to === to : !range))?.key;
  const setDate = (field, value) => {
    if (!value) return;
    const next = { from, to, [field]: value };
    if (next.from > next.to) next[field === 'from' ? 'to' : 'from'] = value;
    setRange(next);
  };

  // Each stand's day, fetched once and kept: { "standId|date": raw | 'error' }
  const [days, setDays] = useState({});
  const requested = useRef(new Set());
  const loaded = useRef(new Set());
  const needed = useMemo(() => inRange.flatMap((g) => chosen.map((s) => ({ stand: s, date: g.date, key: `${s.id}|${g.date}` }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inRange.map((g) => g.date).join(), chosen.map((s) => s.id).join()]);
  useEffect(() => {
    // Today's game is fetched again each time the range or stands change, so its numbers stay current
    const asked = requested.current;
    const done = loaded.current;
    const queue = needed.filter((n) => !asked.has(n.key) || n.date === todayKey);
    if (!queue.length) return undefined;
    let cancelled = false;
    queue.forEach((n) => asked.add(n.key));
    let next = 0;
    const worker = async () => {
      while (!cancelled && next < queue.length) {
        const n = queue[next];
        next += 1;
        try {
          const raw = await fetchLocationDay('concession', n.stand, authCtx, n.date);
          if (!cancelled) {
            done.add(n.key);
            setDays((prev) => ({ ...prev, [n.key]: raw }));
          }
        } catch (error) {
          if (error?.status === 401) {
            cancelled = true;
            authCtx.logout();
            navigate('/auth');
            return;
          }
          if (!cancelled) {
            done.add(n.key);
            setDays((prev) => ({ ...prev, [n.key]: 'error' }));
          }
        }
      }
    };
    Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
    return () => {
      cancelled = true;
      // Anything not loaded yet is asked for again next time
      queue.forEach((n) => { if (!done.has(n.key)) asked.delete(n.key); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needed]);
  const loadedCount = needed.filter((n) => n.key in days).length;
  const loading = loadedCount < needed.length;

  // Alerts per game, once every chosen stand's day for that game has loaded
  const rows = useMemo(() => inRange.map((g) => {
    const ready = chosen.every((s) => `${s.id}|${g.date}` in days);
    if (!ready) return { ...g, ready: false };
    const byStand = {};
    chosen.forEach((s) => { const raw = days[`${s.id}|${g.date}`]; if (raw && raw !== 'error') byStand[s.id] = raw; });
    const { alerts, hasData } = gameAlerts(chosen, byStand);
    return { ...g, ready: true, hasData, alerts, stats: summarizeAlerts(alerts), live: g.date === todayKey };
  }), [inRange, chosen, days, todayKey]);
  const withData = rows.filter((r) => r.ready && r.hasData);
  const allAlerts = withData.flatMap((r) => r.alerts);
  const total = summarizeAlerts(allAlerts);
  const gamesWithAlerts = withData.filter((r) => r.stats.count > 0).length;

  const opponentOf = (series) => getTeam(league, series?.teamID)?.name || null;
  const [openRow, setOpenRow] = useState(null);

  // Trend: alerts per game (bars) and the average response time (line)
  const chart = useMemo(() => {
    const labels = withData.map((r) => moment(r.date).format('MMM D'));
    const responses = withData.map((r) => (r.stats.avg != null ? Math.round(r.stats.avg * 10) / 10 : null));
    const options = applyChartTheme({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { autoSkip: true, maxRotation: 0 } },
        y: { stacked: true, position: 'left', beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Alerts', color: ALERT_COLOR } },
        y1: {
          position: 'right',
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          title: { display: true, text: 'Avg response (min)', color: RESPONSE_COLOR },
          ticks: { callback: (v) => `${v}m` },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const r = withData[items[0]?.dataIndex];
              if (!r) return '';
              const opp = opponentOf(r.series);
              return `${moment(r.date).format('ddd, MMM D')}${opp ? ` vs ${opp}` : ''}${r.live ? ' (so far)' : ''}`;
            },
            label: (item) => {
              if (item.dataset.yAxisID === 'y1') return ` Avg response: ${item.raw == null ? 'no recoveries' : formatResponse(item.raw)}`;
              return item.dataset.label === 'Blips' ? ` Blips: ${item.raw}` : ` Alerts (not blips): ${item.raw}`;
            },
          },
        },
      },
    });
    return {
      options,
      data: {
        labels,
        datasets: [
          {
            type: 'line',
            label: 'Avg response',
            data: responses,
            yAxisID: 'y1',
            borderColor: RESPONSE_COLOR,
            backgroundColor: RESPONSE_COLOR,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            spanGaps: true,
            tension: 0.25,
            order: 1,
          },
          {
            type: 'bar',
            label: 'Alerts',
            data: withData.map((r) => r.stats.real),
            yAxisID: 'y',
            stack: 'alerts',
            backgroundColor: 'rgba(232, 133, 89, 0.55)',
            maxBarThickness: 28,
            order: 2,
          },
          {
            type: 'bar',
            label: 'Blips',
            data: withData.map((r) => r.stats.blips),
            yAxisID: 'y',
            stack: 'alerts',
            backgroundColor: BLIP_COLOR,
            maxBarThickness: 28,
            order: 3,
          },
        ],
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withData]);

  if (!stands.length) {
    return (
      <Panel>
        <PanelHeader title="Queue alerts" subtitle="No concession stands measure queue times, so there are no alerts to show." />
      </Panel>
    );
  }

  return (
    <>
      {/* Range and stands */}
      <div className="mb-4 cx-ov-bar-wrap">
        <Panel className="cx-ov-bar an-bar">
          <div className="an-bar-row">
            <div className="cx-ov-bar-label">Games</div>
            <div className="qa-range">
              <input type="date" className="qa-date" value={from} min={first} max={last} onChange={(e) => setDate('from', e.target.value)} aria-label="From" />
              <span className="qa-range-sep">to</span>
              <input type="date" className="qa-date" value={to} min={first} max={last} onChange={(e) => setDate('to', e.target.value)} aria-label="To" />
              <div className="cx-chart-segmented" role="group" aria-label="Range presets">
                {presets.map((p) => (
                  <button key={p.key} type="button" className={`cx-chart-segment ${activePreset === p.key ? 'active' : ''}`} onClick={() => setRange(p.range)}>
                    {p.label}
                  </button>
                ))}
              </div>
              <span className="qa-range-count">{inRange.length} game{inRange.length === 1 ? '' : 's'} · {season} season</span>
            </div>
          </div>
          {stands.length > 1 && (
            <div className="an-bar-row">
              <div className="cx-ov-bar-label">Stands</div>
              <div className="qa-stands">
                {stands.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`qa-stand ${chosen.some((c) => c.id === s.id) ? 'active' : ''}`}
                    onClick={() => toggleStand(s.id)}
                    aria-pressed={chosen.some((c) => c.id === s.id)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      {loading && (
        <div className="qa-loading">
          <Icon name="arrow-repeat" /> Loading games {Math.min(loadedCount, needed.length)} of {needed.length}…
        </div>
      )}

      {!inRange.length ? (
        <Panel>
          <PanelHeader title="No games" subtitle="There are no games in this date range." />
        </Panel>
      ) : (
        <>
          <div className="cx-kpi-grid cx-kpi-grid-auto mb-4">
            <KpiCard label="Alerts sent" info={RULE_TEXT} value={total.count.toLocaleString()}>
              <div className="cx-kpi-foot">
                {withData.length ? `${(total.count / withData.length).toFixed(1)} per game · ${gamesWithAlerts} of ${withData.length} games` : 'No games with data yet'}
                {total.blips > 0 && <> · <span className="qa-blip-note" title={BLIP_TEXT}>{total.blips} blip{total.blips === 1 ? '' : 's'}</span></>}
              </div>
            </KpiCard>
            <KpiCard
              label="Avg response time"
              info={`From the alert to the recovery: how long the queue stayed at ${ALERT_AT} min or more (or overflowing) after the alert went out. Blips (back under within a minute) are left out.`}
              value={formatResponse(total.avg)}
              accent={RESPONSE_COLOR}
            >
              <div className="cx-kpi-foot">Median {formatResponse(total.median)}</div>
            </KpiCard>
            <KpiCard
              label={`Back under ${ALERT_AT} min within 10 min`}
              info="Share of recovered alerts where the queue was back under the target within 10 minutes of the alert. Blips are left out."
              value={total.recovered ? `${Math.round((total.within10 / total.recovered) * 100)}%` : '—'}
            >
              <div className="cx-kpi-foot">{total.within10} of {total.recovered} recoveries</div>
            </KpiCard>
            <KpiCard
              label="Longest response"
              info="The slowest recovery in the range (blips left out)."
              value={formatResponse(total.longest)}
            >
              <div className="cx-kpi-foot">
                {total.open > 0 ? `${total.open} alert${total.open === 1 ? '' : 's'} never recovered` : 'Every alert recovered'}
              </div>
            </KpiCard>
          </div>

          {withData.length > 0 && (
            <Panel className="mb-4">
              <PanelHeader
                title="Alerts and response time by game"
                subtitle="Alerts per game (bars, blips in grey) and the average time from alert to recovery, blips left out (line). Oldest game first."
                actions={(
                  <span className="rx-conv-trend-keys">
                    <span><i style={{ background: ALERT_COLOR }} />Alerts</span>
                    <span><i style={{ background: BLIP_COLOR }} />Blips</span>
                    <span><i style={{ background: RESPONSE_COLOR }} />Avg response</span>
                  </span>
                )}
              />
              <div style={{ position: 'relative', height: 260 }}>
                <Bar data={chart.data} options={chart.options} plugins={[hoverGuidePlugin]} />
              </div>
            </Panel>
          )}

          <Panel>
            <PanelHeader
              title="Games"
              subtitle="Every game in the range. Select a game to see each alert; blips are greyed out and left out of the response times."
              info={RULE_TEXT}
            />
            <div className="gx-ov-table-wrap">
              <table className="gx-ov-table an-table qa-table">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Alerts</th>
                    <th>Avg response</th>
                    <th>Median</th>
                    <th>Longest</th>
                    <th>Within 10 min</th>
                    <th>Not recovered</th>
                  </tr>
                </thead>
                <tbody>
                  {[...rows].reverse().map((r) => {
                    const opp = opponentOf(r.series);
                    const open = openRow === r.date;
                    const canOpen = r.ready && r.hasData && r.stats.count > 0;
                    return (
                      <Fragment key={r.date}>
                        <tr className={canOpen ? 'qa-row' : undefined} onClick={canOpen ? () => setOpenRow(open ? null : r.date) : undefined}>
                          <td>
                            <div className="qa-game">
                              {canOpen && <Icon name={open ? 'chevron-down' : 'chevron-right'} />}
                              <strong>{moment(r.date).format('ddd, MMM D')}</strong>
                              {opp && <span className="qa-sub">vs {opp}</span>}
                              {r.live && <span className="qa-live">So far</span>}
                            </div>
                          </td>
                          {!r.ready ? (
                            <td colSpan={6} className="qa-muted">Loading…</td>
                          ) : !r.hasData ? (
                            <td colSpan={6} className="qa-muted">No queue data</td>
                          ) : (
                            <>
                              <td>
                                <strong>{r.stats.count}</strong>
                                {r.stats.blips > 0 && <span className="qa-blip-note"> ({r.stats.blips} blip{r.stats.blips === 1 ? '' : 's'})</span>}
                              </td>
                              <td>{formatResponse(r.stats.avg)}</td>
                              <td>{formatResponse(r.stats.median)}</td>
                              <td>{formatResponse(r.stats.longest)}</td>
                              <td>{r.stats.recovered ? `${r.stats.within10} of ${r.stats.recovered}` : '—'}</td>
                              <td>{r.stats.open ? <span className="qa-open">{r.stats.open}{r.live ? ' (open)' : ''}</span> : '0'}</td>
                            </>
                          )}
                        </tr>
                        {open && (
                          <tr className="qa-detail">
                            <td colSpan={7}>
                              <ul className="qa-alerts">
                                {r.alerts.map((a) => (
                                  <li key={`${a.standId}-${a.sectionId}-${a.start}`} className={isBlip(a) ? 'qa-blip' : undefined} title={isBlip(a) ? BLIP_TEXT : undefined}>
                                    <span className="qa-when">{alertClock(a.start)} – {a.end ? alertClock(a.end) : (r.live ? 'now' : 'end of data')}</span>
                                    <span className="qa-where">{chosen.length > 1 ? `${a.standName} · ` : ''}Section {a.sectionId}</span>
                                    <span className="qa-resp">
                                      {a.minutes != null ? formatResponse(a.minutes) : 'Not recovered'}
                                      {isBlip(a) && <span className="qa-blip-tag">Blip</span>}
                                    </span>
                                    <span className="qa-peak">Peak {Math.round(a.peak)}{a.peakOverflow ? '+' : ''} min{a.peakOverflow ? ' (overflow)' : ''}</span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  );
}
